import { supabase } from '../supabase';
import PAYMENT_LINKS from './stripePaymentLink';
import { createCheckoutSession } from './stripeCheckout';

interface RegistrationDetails {
  memberId: string;
  tournamentId: string;
  amount: number;
  isMember: boolean;
  isLate: boolean;
  membershipType?: 'student' | 'single' | 'family' | null;
  notes?: string;
}

interface CreatePaymentResponse {
  success: boolean;
  paymentId?: string;
  url?: string;
  error?: string;
}

async function createPaymentRecord(details: RegistrationDetails): Promise<string> {
  try {
    // Log cost breakdown
    const amountInDollars = details.amount / 100;
    console.log('Payment details:', {
      baseCost: `$${details.isMember ? '150.00' : '200.00'}`,
      membershipCost: details.membershipType === 'student' ? '$100.00' :
                     details.membershipType === 'single' ? '$150.00' :
                     details.membershipType === 'family' ? '$250.00' : '$0.00',
      lateFee: details.isLate ? '$20.00' : '$0.00',
      totalAmount: `$${amountInDollars.toFixed(2)}`,
      amountInCents: details.amount,
      ...details
    });

    // Check for existing active payment
    const { data: existingPayment, error: checkError } = await supabase
      .from('tournament_payments')
      .select()
      .eq('tournament_id', details.tournamentId)
      .eq('member_id', details.memberId)
      .in('status', ['pending', 'paid'])
      .maybeSingle();

    if (checkError) throw checkError;

    if (existingPayment?.status === 'paid') {
      throw new Error('Already registered for this tournament');
    }

    let payment;
    
    if (existingPayment?.status === 'pending') {
      console.log('Updating existing payment:', existingPayment.id);
      const { data: updatedPayment, error: updateError } = await supabase
        .from('tournament_payments')
        .update({
          amount: details.amount,
          notes: details.membershipType ? `Includes ${details.membershipType} membership` : undefined,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingPayment.id)
        .select()
        .single();

      if (updateError) throw updateError;
      payment = updatedPayment;
    } else {
      console.log('Creating new payment record');
      const { data: newPayment, error: insertError } = await supabase
        .from('tournament_payments')
        .insert({
          tournament_id: details.tournamentId,
          member_id: details.memberId,
          amount: details.amount,
          status: 'pending',
          payment_method: 'stripe',
          notes: details.notes || (details.membershipType ? `Includes ${details.membershipType} membership` : undefined)
        })
        .select()
        .single();

      if (insertError) throw insertError;
      payment = newPayment;
    }

    if (!payment) {
      throw new Error('Failed to create/update payment record');
    }

    return payment.id;
  } catch (error) {
    console.error('Failed to create direct payment link:', error);
    throw error;
  }
}

async function getDirectPaymentLink(paymentId: string, details: RegistrationDetails): Promise<CreatePaymentResponse> {
  try {
    const amountInDollars = details.amount / 100;
    console.log('Creating payment session with breakdown:', {
      totalAmount: `$${amountInDollars.toFixed(2)}`,
      amountInCents: details.amount,
      ...details
    });
    
    let response: CreatePaymentResponse;

    try {
      // Try Stripe Checkout first
      const { data: session, error: sessionError } = await supabase
        .functions.invoke('create-stripe-session', {
          body: {
            tournament_id: details.tournamentId,
            member_id: details.memberId,
            amount: details.amount,
            metadata: {
              membership_type: details.membershipType,
              is_late: details.isLate
            }
          }
        });

      if (!sessionError && session?.stripe_session_url) {
        console.log('Created Stripe session:', {
          paymentId,
          url: session.stripe_session_url
        });
        
        // Update payment record with session info
        await supabase
          .from('tournament_payments')
          .update({
            stripe_session_id: session.stripe_session_id,
            stripe_session_url: session.stripe_session_url
          })
          .eq('id', paymentId);

        return {
          success: true,
          paymentId,
          url: session.stripe_session_url,
          sessionId: session.stripe_session_id
        };
      }

      // Log failure and continue to fallback
      console.warn('Stripe session creation failed:', 
        sessionError ? `Error: ${sessionError.message}` : 'No session URL returned'
      );
    } catch (error) {
      // Log error and continue to fallback
      console.warn('Error creating Stripe session:', error);
    }

    // Use direct payment link as fallback
    console.log('Using direct payment link as fallback');
    let baseUrl = details.membershipType
      ? PAYMENT_LINKS[`${details.membershipType}_membership${details.isLate ? '_late' : ''}`]
      : PAYMENT_LINKS[`${details.isMember ? 'member' : 'non_member'}${details.isLate ? '_late' : ''}`];

    // Add query parameters
    const params = new URLSearchParams();
    params.append('client_reference_id', paymentId);
    params.append('success_url', `${window.location.origin}/?payment=success`);
    params.append('cancel_url', `${window.location.origin}/?payment=cancel`);
    
    console.log('Direct payment link details:', {
      baseUrl,
      params: Object.fromEntries(params)
    });

    // Update payment record to indicate direct link usage
    await supabase
      .from('tournament_payments')
      .update({
        payment_method: 'stripe_direct',
        stripe_session_url: `${baseUrl}?${params.toString()}`
      })
      .eq('id', paymentId);

    return {
      success: true,
      paymentId,
      url: `${baseUrl}?${params.toString()}`
    };

  } catch (error) {
    console.error('Payment link creation failed:', 
      error instanceof Error ? error.message : 'Unknown error'
    );
    throw error;
  }
}

export async function createPaymentSession(details: RegistrationDetails): Promise<CreatePaymentResponse> {
  try {
    console.log('Creating payment session:', details);

    // Create payment record first
    const paymentId = await createPaymentRecord(details);
    
    // Get direct payment link
    return getDirectPaymentLink(paymentId, details);
  } catch (error) {
    console.error('Failed to create payment session:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create payment session'
    };
  }
}

export async function completeRegistration() {
  try {
    // Check URL for session ID first
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      try {
        // Verify session status
        const { data: session, error } = await supabase
          .rpc('get_stripe_session_status', { 
            p_session_id: sessionId 
          });

        if (error) throw error;

        if (session?.payment_status === 'paid') {
          return {
            success: true,
            memberId: session.member_id,
            tournamentId: session.tournament_id
          };
        }
      } catch (error) {
        console.error('Failed to verify session:', error);
      }
    }
    
    // Fall back to checking stored registration
    const stored = localStorage.getItem('pending_registration');
    if (!stored) {
      console.log('No pending registration found');
      return null;
    }

    try {
      const registration = JSON.parse(stored);
      const { memberId, tournamentId, amount, timestamp } = registration;

      console.log('Processing registration:', {
        memberId,
        tournamentId,
        amount,
        timestamp
      });

      // Verify registration hasn't expired (30 min timeout)
      if (Date.now() - timestamp > 30 * 60 * 1000) {
        console.warn('Registration expired:', {
          timestamp,
          now: Date.now(),
          age: Math.round((Date.now() - timestamp) / 60000) + ' minutes'
        });

        // Update payment status to expired
        await supabase
          .from('tournament_payments')
          .update({ 
            status: 'expired',
            updated_at: new Date().toISOString()
          })
          .eq('tournament_id', tournamentId)
          .eq('member_id', memberId)
          .eq('status', 'pending');

        throw new Error('Registration expired');
      }

      // Clear pending registration
      localStorage.removeItem('pending_registration');

      return {
        success: true,
        memberId,
        tournamentId
      };
    } catch (error) {
      console.error('Error parsing pending registration:', error);
      localStorage.removeItem('pending_registration');
      throw error;
    }

  } catch (error) {
    console.error('Failed to complete registration:', error);
    throw error;
  }
}